import axios from "axios";

/*
 * Two sessions can be open in the same browser at once: a customer on their own account,
 * and one of us on the admin screens. They are stored under different keys and sent on
 * different paths, so logging out of one never signs the other out - and a customer's
 * token is never offered to a /console route, where it would only be refused.
 *
 * Both live in sessionStorage, so a session belongs to one tab and ends when it closes.
 */
const CUSTOMER_KEY = "mx_account_token";
const CONSOLE_KEY = "mx_console_token";

export const tokens = {
    /*
     * Per tab, not per browser.
     *
     * localStorage would keep a customer signed in across every tab and across restarts,
     * which is how most products work - but FDM itself holds its session per tab, and a
     * customer who closes the SCADA and finds the console still open under their name
     * would be right to find that odd. A pasted URL in a second tab therefore asks for a
     * login, and closing the tab ends the session.
     *
     * The cost is real and accepted: Ctrl+click opens a tab that has to sign in again.
     */
    account: {
        get: () => sessionStorage.getItem(CUSTOMER_KEY) || "",
        set: (t) => sessionStorage.setItem(CUSTOMER_KEY, t),
        clear: () => sessionStorage.removeItem(CUSTOMER_KEY),
    },
    console: {
        get: () => sessionStorage.getItem(CONSOLE_KEY) || "",
        set: (t) => sessionStorage.setItem(CONSOLE_KEY, t),
        clear: () => sessionStorage.removeItem(CONSOLE_KEY),
    },
};

const api = axios.create({ baseURL: "/api", timeout: 30000 });

api.interceptors.request.use((config) => {
    const isConsole = String(config.url || "").startsWith("/console");
    const token = isConsole ? tokens.console.get() : tokens.account.get();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

/** Whatever the server said, or something a human can act on. */
export function apiError(e) {
    return e?.response?.data?.error || e?.message || "Something went wrong";
}

export default api;
